import React from 'react';
import { Button, ButtonProps } from '@mui/material';

interface FlashyButtonProps extends ButtonProps {
  flashColors?: [string, string]; // [color1, color2] for the flash gradient
  borderColor?: string;
  textColor?: string;
  pulseInterval?: number; // Pulse animation interval in seconds
  flashInterval?: number; // Flash animation interval in seconds
  flashOpacity?: number; // Flash opacity (0-1)
  flashWidth?: string; // Flash width as percentage
}

/**
 * FlashyButton - Reusable button with pulsing border and left-to-right flash animations
 * 
 * Features:
 * - Pulsing border with scale effect
 * - Left-to-right color flash sweep
 * - Customizable colors, timing, and opacity
 * - Interactive hover speedup
 * - DRY implementation for consistent flashy buttons
 */
const FlashyButton: React.FC<FlashyButtonProps> = ({
  children,
  flashColors = ['#ff69b4', '#9966cc'], // Default pink to purple
  borderColor = 'purple',
  textColor = 'black',
  pulseInterval = 2,
  flashInterval = 2,
  flashOpacity = 0.5,
  flashWidth = '80%',
  sx,
  ...buttonProps
}) => {
  const [color1, color2] = flashColors;
  
  return (
    <Button
      {...buttonProps}
      sx={{
        borderColor: borderColor,
        color: textColor,
        position: 'relative',
        overflow: 'hidden',
        fontWeight: 'bold',
        fontSize: '0.9rem',
        px: 3,
        py: 1.2,
        // Pulsing border animation
        '@keyframes pulse': {
          '0%': {
            transform: 'scale(1)',
            opacity: 1,
            borderColor: borderColor,
          },
          '50%': {
            transform: 'scale(1.05)',
            opacity: 0.8,
            borderColor: `rgba(128, 0, 128, 0.5)`, // TODO: Make this dynamic based on borderColor
          },
          '100%': {
            transform: 'scale(1)',
            opacity: 1,
            borderColor: borderColor,
          },
        },
        // Left to right flash animation
        '@keyframes leftToRightFlash': {
          '0%': {
            transform: 'translateX(-100%)',
            opacity: 0,
          },
          '30%': {
            opacity: flashOpacity,
          },
          '70%': {
            opacity: flashOpacity,
          },
          '100%': {
            transform: 'translateX(100%)',
            opacity: 0,
          },
        },
        animation: `pulse ${pulseInterval}s ease-in-out infinite`,
        // Left to right flash overlay
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: flashWidth,
          height: '100%',
          background: `linear-gradient(90deg, transparent, ${color1} 30%, ${color2} 70%, transparent)`,
          animation: `leftToRightFlash ${flashInterval}s ease-in-out infinite`,
          zIndex: 1,
          pointerEvents: 'none',
        },
        '&:hover': {
          borderColor: borderColor,
          backgroundColor: `rgba(128, 0, 128, 0.1)`, // TODO: Make this dynamic
          animation: `pulse ${pulseInterval / 2}s ease-in-out infinite`, // Faster on hover
          '&::before': {
            animation: `leftToRightFlash ${flashInterval / 2}s ease-in-out infinite`, // Faster flash on hover
          }
        },
        // Ensure button text is above the flash
        '& span': {
          position: 'relative',
          zIndex: 2,
        },
        ...sx, // Allow override of styles
      }}
    >
      {children}
    </Button>
  );
};

export default FlashyButton;
